/* global React, Icon, Button, Badge, EmptyState, Pagination, usePaged, fmt, t, PlummeI18n */
const { useState: recurringUseState, useMemo: recurringUseMemo } = React;

const recurringList = (value) => (Array.isArray(value) ? value : []);
const recurringNumber = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;
const recurringLineNet = (line) => {
  const quantity = recurringNumber(line?.quantity ?? line?.qty) || 1;
  const unitPrice = recurringNumber(line?.unitPrice ?? line?.price);
  const discount = recurringNumber(line?.discountValue ?? line?.discount ?? line?.disc);
  const base = quantity * unitPrice;
  return Math.max(0, line?.discountType === 'amount' || line?.discMode === 'kr'
    ? base - discount
    : base * (1 - discount / 100));
};
const recurringTotals = (agreement) => recurringList(agreement?.invoiceTemplate?.lines).reduce((totals, line) => {
  const net = recurringLineNet(line);
  const vat = net * recurringNumber(line?.vatRate ?? line?.vat) / 100;
  return { net: totals.net + net, gross: totals.gross + net + vat };
}, { net: 0, gross: 0 });
const recurringDateLabel = (value) => {
  const day = String(value || '').slice(0, 10);
  if (!day) return '–';
  return PlummeI18n.date(day, { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const recurringIntervalLabel = (value) => t(`agreements.interval.${value || 'monthly'}`);
// Mirrors hasSendableCustomerEmail in functions/recurringBilling/recurringBilling.ts:
// the schedule blocks occurrences whose customer fails this check.
const recurringHasSendableEmail = (customer) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(customer?.email || '').trim());
// Same customer resolution as the schedule: the live customer record wins over
// the template snapshot, so fixing the email on the customer clears the warning.
const recurringAgreementCustomer = (agreement, customers) => {
  const template = agreement?.invoiceTemplate?.customer || {};
  const id = agreement?.customerId || template.id;
  return recurringList(customers).find((customer) => id && customer?.id === id && customer?.status !== 'archived') || template;
};
const RECURRING_ERROR_KEYS = {
  'missing-customer-email': 'agreements.error.missingCustomerEmail',
  'missing-customer': 'agreements.error.missingCustomer',
};
const recurringErrorText = (entry) => {
  const key = RECURRING_ERROR_KEYS[entry?.lastErrorCode || entry?.errorCode];
  return key ? t(key) : String(entry?.lastError || '');
};
function RecurringMissingEmailWarning({ compact = false }) {
  if (compact) return <Badge variant="warning" title={t('agreements.warning.missingCustomerEmailHelp')}>{t('agreements.warning.missingCustomerEmail')}</Badge>;
  return <div className="alert alert-warning"><Icon name="alert" size={17} /><span><b>{t('agreements.warning.missingCustomerEmail')}</b> {t('agreements.warning.missingCustomerEmailHelp')}</span></div>;
}
const recurringAgreementStatus = (agreement) => {
  if (agreement?.lastError) return { key: 'failed', label: t('agreements.status.failed'), variant: 'danger' };
  if (agreement?.status === 'pause' || agreement?.status === 'paused') return { key: 'pause', label: t('agreements.status.paused'), variant: 'warning' };
  if (agreement?.status === 'ended' || agreement?.active === false) return { key: 'ended', label: t('agreements.status.ended'), variant: 'muted' };
  return { key: 'active', label: t('agreements.status.active'), variant: 'success' };
};
const recurringSortValue = (agreement, key) => {
  const totals = recurringTotals(agreement);
  const customer = agreement?.invoiceTemplate?.customer || {};
  const status = recurringAgreementStatus(agreement);
  switch (key) {
    case 'customer': return String(customer.name || agreement?.name || '').toLocaleLowerCase('nb-NO');
    case 'lastSent': return String(agreement?.lastSentAt || '');
    case 'nextDelivery': return String(agreement?.nextInvoiceDate || '');
    case 'net': return totals.net;
    case 'amount': return totals.gross;
    case 'status': return status.key;
    default: return String(agreement?.nextInvoiceDate || '');
  }
};

function RecurringSortHead({ sort, sortKey, children, align = 'left', onSort }) {
  const active = sort.key === sortKey;
  return <span style={{ textAlign: align }}>
    <button
      type="button"
      className={'th-sort' + (active ? ' on' : '')}
      onClick={() => onSort(sortKey)}
      aria-label={t('agreements.sortBy', { column: typeof children === 'string' ? children : '' })}
      aria-pressed={active}
    >
      <span>{children}</span><span className="th-arrow">{active ? (sort.dir === 1 ? '↑' : '↓') : '↕'}</span>
    </button>
  </span>;
}

function RecurringAgreementRow({ agreement, runs, invoices, customers, onStatusChange }) {
  const [expanded, setExpanded] = recurringUseState(false);
  const totals = recurringTotals(agreement);
  const status = recurringAgreementStatus(agreement);
  const customer = recurringAgreementCustomer(agreement, customers);
  const missingEmail = agreement.automaticSending !== false && !recurringHasSendableEmail(customer)
    && agreement.lastErrorCode !== 'missing-customer';
  const history = recurringList(runs)
    .filter((run) => run.groupId === agreement.id)
    .sort((left, right) => String(right.sentAt || right.createdAt || '').localeCompare(String(left.sentAt || left.createdAt || '')));
  const invoiceById = new Map(recurringList(invoices).map((invoice) => [invoice.id, invoice]));
  const canResume = status.key === 'pause' || status.key === 'failed';
  return <div className={'agreement-row' + (expanded ? ' is-open' : '')}>
    <button type="button" className="agreement-row-main" data-help={t('helpTopics.recurring.body')} data-help-title={t('helpTopics.recurring.title')} onClick={() => setExpanded((open) => !open)} aria-expanded={expanded} data-help={t('helpTopics.recurring.body')} data-help-title={t('helpTopics.recurring.title')}>
      <span className="agreement-chevron"><Icon name={expanded ? 'chevUp' : 'chevDown'} size={16} /></span>
      <span className="agreement-customer"><b>{customer.name || t('agreements.unknownCustomer')}</b><small>{agreement.name || t('agreements.recurringInvoice')}</small>{missingEmail && <RecurringMissingEmailWarning compact />}</span>
      <span><small>{t('agreements.lastSent')}</small><b>{recurringDateLabel(agreement.lastSentAt)}</b></span>
      <span  ><small>{t('agreements.nextDelivery')}</small><b>{recurringDateLabel(agreement.nextInvoiceDate)}</b></span>
      <span className="agreement-money"><small>{t('agreements.net')}</small><b>{fmt(totals.net)}</b></span>
      <span className="agreement-money"><small>{t('agreements.amount')}</small><b>{fmt(totals.gross)}</b></span>
      <Badge variant={status.variant}>{status.label}</Badge>
    </button>
    <div className="agreement-row-action">
      {status.key !== 'ended' && <Button size="sm" variant={canResume ? 'primary' : 'secondary'} onClick={() => onStatusChange(agreement, canResume ? 'active' : 'pause')} data-help={t('helpTopics.recurringAction.body')} data-help-title={t('helpTopics.recurringAction.title')}>
        {canResume ? t('agreements.resume') : t('agreements.pause')}
      </Button>}
    </div>
    {expanded && <div className="agreement-details">
      <div className="agreement-summary">
        <span><small>{t('agreements.interval')}</small><b>{recurringIntervalLabel(agreement.interval)}</b></span>
        <span><small>{t('agreements.paymentTerms')}</small><b>{t('agreements.days', { count: agreement.dueDays ?? 14 })}</b></span>
        <span><small>{t('agreements.net')}</small><b>{fmt(totals.net)}</b></span>
        <span><small>{t('agreements.total')}</small><b>{fmt(totals.gross)}</b></span>
      </div>
      <div className="agreement-lines">
        {recurringList(agreement.invoiceTemplate?.lines).map((line, index) => <div className="agreement-line" key={line.id || index}>
          <span><b>{line.description || t('agreements.invoiceLine')}</b><small>{recurringNumber(line.quantity ?? line.qty) || 1} {t('agreements.units')} × {fmt(recurringNumber(line.unitPrice ?? line.price))}</small></span>
          <b>{fmt(recurringLineNet(line))}</b>
        </div>)}
      </div>
      <div className="agreement-history">
        <b>{t('agreements.deliveries')}</b>
        {history.length === 0 ? <p className="t-sm muted">{t('agreements.noDeliveries')}</p> : history.map((run) => {
          const invoice = invoiceById.get(run.invoiceId);
          return <div className="agreement-history-row" key={run.id}>
            <span>{recurringDateLabel(run.sentAt || run.occurrenceDate)} · {t('agreements.invoice')} {run.invoiceNumber || invoice?.invoiceNumber || '–'}</span>
            <Badge variant={run.status === 'sent' ? 'success' : run.status === 'failed' || run.status === 'blocked' ? 'danger' : 'neutral'} title={run.status === 'blocked' ? recurringErrorText(run) : undefined}>{run.status === 'sent' ? t('agreements.sent') : run.status === 'failed' ? t('agreements.status.failed') : run.status === 'blocked' ? t('agreements.notSent') : t('agreements.processing')}</Badge>
          </div>;
        })}
      </div>
      {agreement.lastError ? <div className="alert alert-error"><Icon name="alert" size={17} /><span>{recurringErrorText(agreement)}</span></div> : missingEmail && <RecurringMissingEmailWarning />}
    </div>}
  </div>;
}

function RecurringAgreementsList({ model, search, onStatusChange }) {
  const [sort, setSort] = recurringUseState({ key: 'nextDelivery', dir: 1 });
  const onSort = (key) => setSort((current) => current.key === key ? { key, dir: current.dir * -1 } : { key, dir: key === 'customer' || key === 'status' ? 1 : -1 });
  const agreements = recurringUseMemo(() => {
    const query = String(search || '').trim().toLowerCase();
    return recurringList(model?.groups)
      .filter((agreement) => agreement?.type === 'recurring' && agreement?.status !== 'ended' && agreement?.active !== false)
      .filter((agreement) => {
        if (!query) return true;
        const totals = recurringTotals(agreement);
        const customer = agreement.invoiceTemplate?.customer || {};
        return [customer.name, customer.email, agreement.name, agreement.description, agreement.interval, totals.net, totals.gross]
          .some((value) => String(value || '').toLowerCase().includes(query));
      })
      .sort((left, right) => {
        const leftValue = recurringSortValue(left, sort.key);
        const rightValue = recurringSortValue(right, sort.key);
        if (typeof leftValue === 'number' || typeof rightValue === 'number') return ((Number(leftValue) || 0) - (Number(rightValue) || 0)) * sort.dir;
        return String(leftValue || '').localeCompare(String(rightValue || ''), 'nb-NO', { numeric: true }) * sort.dir;
      });
  }, [model, search, sort]);
  const page = usePaged(agreements, 10);

  if (!agreements.length) return <EmptyState noVisual title={t('agreements.emptyTitle')} text={t('agreements.emptyText')} />;
  return <React.Fragment>
    <div className="agreement-list">
      <div className="agreement-list-head">
        <RecurringSortHead sort={sort} sortKey="customer" onSort={onSort}>{t('agreements.customer')}</RecurringSortHead>
        <RecurringSortHead sort={sort} sortKey="lastSent" onSort={onSort}>{t('agreements.lastSent')}</RecurringSortHead>
        <RecurringSortHead sort={sort} sortKey="nextDelivery" onSort={onSort}>{t('agreements.nextDelivery')}</RecurringSortHead>
        <RecurringSortHead sort={sort} sortKey="net" align="right" onSort={onSort}>{t('agreements.net')}</RecurringSortHead>
        <RecurringSortHead sort={sort} sortKey="amount" align="right" onSort={onSort}>{t('agreements.amount')}</RecurringSortHead>
        <RecurringSortHead sort={sort} sortKey="status" onSort={onSort}>{t('agreements.status')}</RecurringSortHead>
        <span />
      </div>
      {page.slice.map((agreement) => <RecurringAgreementRow
        key={agreement.id}
        agreement={agreement}
        runs={model.invoiceRuns}
        invoices={model.invoices}
        customers={model.customers}
        onStatusChange={onStatusChange}
      />)}
    </div>
    {page.total > 0 && <div className="mt-16"><Pagination {...page} onPage={page.setPage} pageSize={page.ps} onPageSize={(size) => { page.setPs(size); page.setPage(1); }} pageSizes={[10, 25, 50]} /></div>}
  </React.Fragment>;
}

Object.assign(window, { RecurringAgreementsList, RecurringMissingEmailWarning, recurringHasSendableEmail, recurringErrorText });
